import React, { useState } from 'react';
import { Search, Plus, Download, Upload, AlertTriangle, CheckCircle, Truck, FileText, Calculator } from 'lucide-react';
import { Button } from './Button';

const AdvancedGSTFeatures = () => {
    const [activeTab, setActiveTab] = useState('hsn-sac');

    const [hsnCodes] = useState([
        {
            id: '1',
            code: '8471',
            description: 'Automatic data processing machines',
            gstRate: 18,
            category: 'Electronics',
            lastUsed: '2024-01-15'
        },
        {
            id: '2',
            code: '9403',
            description: 'Other furniture and parts thereof',
            gstRate: 12,
            category: 'Furniture',
            lastUsed: '2024-01-10'
        }
    ]);

    const [eWayBills] = useState([
        {
            id: '1',
            billNumber: 'EWB001',
            client: 'ABC Industries',
            vehicleNumber: 'MH12AB1234',
            distance: 250,
            value: 125000,
            status: 'In Transit',
            generatedDate: '2024-01-15',
            validUntil: '2024-01-16'
        },
        {
            id: '2',
            billNumber: 'EWB002',
            client: 'XYZ Corp',
            vehicleNumber: 'DL01CD5678',
            distance: 180,
            value: 85000,
            status: 'Delivered',
            generatedDate: '2024-01-14',
            validUntil: '2024-01-15'
        }
    ]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Generated': return 'bg-blue-100 text-blue-800';
            case 'In Transit': return 'bg-yellow-100 text-yellow-800';
            case 'Delivered': return 'bg-green-100 text-green-800';
            case 'Cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const tabs = [
        { id: 'hsn-sac', label: 'HSN/SAC Codes', icon: FileText },
        { id: 'tax-rates', label: 'Tax Rate Changes', icon: Calculator },
        { id: 'eway-bills', label: 'E-Way Bills', icon: Truck },
        { id: 'gstr2a-reconciliation', label: 'GSTR-2A Reconciliation', icon: CheckCircle }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Advanced GST Features</h1>
                    <div className="flex space-x-3">

                        <Button label="Export Data" />
                        <Button label="Add New" />

                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <tab.icon className="h-4 w-4" />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* HSN/SAC Codes Management */}
            {activeTab === 'hsn-sac' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">HSN/SAC Code Management</h3>
                            <div className="flex space-x-2">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search HSN/SAC codes..."
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <Button label="Add Code" />

                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            HSN/SAC Code
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            GST Rate
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Used
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {hsnCodes.map((code) => (
                                        <tr key={code.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {code.code}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {code.description}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {code.gstRate}%
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {code.category}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {code.lastUsed}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                                                <button className="text-red-600 hover:text-red-900">Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Auto-suggestions */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Smart Suggestions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h4 className="font-medium text-blue-900">Most Used Codes</h4>
                                <p className="text-2xl font-bold text-blue-600">15</p>
                                <p className="text-sm text-blue-700">Quick access available</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4">
                                <h4 className="font-medium text-green-900">Auto-detected</h4>
                                <p className="text-2xl font-bold text-green-600">8</p>
                                <p className="text-sm text-green-700">From invoice descriptions</p>
                            </div>
                            <div className="bg-orange-50 rounded-lg p-4">
                                <h4 className="font-medium text-orange-900">Needs Review</h4>
                                <p className="text-2xl font-bold text-orange-600">3</p>
                                <p className="text-sm text-orange-700">Uncertain classifications</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tax Rate Changes */}
            {activeTab === 'tax-rates' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Tax Rate Change Notifications</h3>
                    <div className="space-y-4">
                        <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                            <div className="flex items-start">
                                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                                <div>
                                    <h4 className="font-medium text-yellow-900">Rate Change Alert</h4>
                                    <p className="text-sm text-yellow-800 mt-1">
                                        GST rate for HSN 8471 (Computer hardware) changed from 18% to 12% effective from January 1, 2024
                                    </p>
                                    <div className="mt-2 flex space-x-2">
                                        <button className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700">
                                            Update Affected Invoices
                                        </button>
                                        <button className="bg-white text-yellow-600 border border-yellow-600 px-3 py-1 rounded text-sm hover:bg-yellow-50">
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
                            <div className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
                                <div>
                                    <h4 className="font-medium text-green-900">Successfully Updated</h4>
                                    <p className="text-sm text-green-800 mt-1">
                                        12 invoices updated with new tax rates for December 2023 billing
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* E-Way Bills */}
            {activeTab === 'eway-bills' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">E-Way Bill Management</h3>
                            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                                <Plus className="h-4 w-4" />
                                <span>Generate E-Way Bill</span>
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Bill Number
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Client
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Vehicle
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Distance
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Value
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Valid Until
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {eWayBills.map((bill) => (
                                    <tr key={bill.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {bill.billNumber}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {bill.client}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {bill.vehicleNumber}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {bill.distance} km
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{bill.value.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(bill.status)}`}>
                                                {bill.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {bill.validUntil}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* GSTR-2A Reconciliation */}
            {activeTab === 'gstr2a-reconciliation' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">GSTR-2A Reconciliation</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-blue-50 rounded-lg p-4">
                                <h4 className="font-medium text-blue-900">Auto-matched</h4>
                                <p className="text-2xl font-bold text-blue-600">156</p>
                                <p className="text-sm text-blue-700">Purchase invoices</p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-4">
                                <h4 className="font-medium text-yellow-900">Pending Review</h4>
                                <p className="text-2xl font-bold text-yellow-600">12</p>
                                <p className="text-sm text-yellow-700">Require manual verification</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-4">
                                <h4 className="font-medium text-red-900">Mismatched</h4>
                                <p className="text-2xl font-bold text-red-600">3</p>
                                <p className="text-sm text-red-700">Need correction</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h4 className="font-medium text-gray-900 mb-4">Reconciliation Actions</h4>
                        <div className="space-x-4">
                            <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                                Auto-reconcile Matches
                            </button>
                            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                                Download GSTR-2A
                            </button>
                            <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                                Generate Report
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvancedGSTFeatures;